import { TextField } from "@kobalte/core/text-field"
import { postContentClass } from "@repo/fika-blog/src/classes"
import { postContainerClass } from "@repo/fika-blog/src/content.css"
import {
  carouselArrowClass,
  carouselArrowLeftClass,
  carouselArrowRightClass,
  carouselDotClass,
  carouselDotsClass,
  carouselSlideClass,
  carouselTrackClass,
  postDescriptionClass,
  postHeaderClass,
  postHeaderContentClass,
  postTitleClass,
} from "@repo/fika-blog/src/post-header.css"
import type { Editor } from "@repo/fika-blog/src/tiptap/core"
import type { Transaction } from "@repo/fika-blog/src/tiptap/pm/state"
import { getImageHeaderUrls } from "@repo/fika-shared/src/lib/image-header-url"
import { splitUrl } from "@repo/fika-shared/src/lib/slug.js"
import type { Locale } from "@repo/fika-shared/src/locale"
import { createEventListener } from "@solid-primitives/event-listener"
import { usePageVisibility } from "@solid-primitives/page-visibility"
import { createFileUploader } from "@solid-primitives/upload"
import { useBeforeLeave, useParams } from "@solidjs/router"
import { createMutation, createQuery } from "@urql/solid"
import { format } from "date-fns"
import {
  type Accessor,
  children,
  createEffect,
  createMemo,
  createSignal,
  For,
  Match,
  on,
  onCleanup,
  type ParentProps,
  Show,
  Switch,
} from "solid-js"
import { createEditorTransaction, useEditorIsEmpty } from "solid-tiptap"
import { useApplication } from "@/context/session-provider"
import { uploadImage } from "@/data/upload-image"
import { Box } from "@/designSystem/components/box"
import { Button, LinkButton } from "@/designSystem/components/button"
import { Icon } from "@/designSystem/components/icon"
import { Tooltip } from "@/designSystem/components/tooltip"
import { type DrawerItem, MainLayout } from "@/designSystem/layouts/main-layout"
import {
  focusModeButtonWrapperClass,
  itemLinkClass,
} from "@/designSystem/layouts/main-layout.css"
import { FileUpload } from "@/designSystem/patterns/file-upload"
import { Pills } from "@/designSystem/patterns/pills"
import {
  PostStatusPillFragment,
  postStatusPill,
} from "@/designSystem/patterns/post-status-pill"
import {
  createFragment,
  type FragmentOf,
  graphql,
  readFragment,
} from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import { defaultJsonContent } from "@/lib/default-json-content"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { createScheduledAsync } from "@/lib/scheduled-async"
import { toast } from "@/lib/toast"
import { useVirtualKeyboard } from "@/lib/use-virtual-keyboard"
import { createBlogRouter } from "@/routes/posts-router"
import { LanguageBanner } from "./components/language-banner"
import { TopToolbar } from "./editor/menus/top-toolbar"
import { createEditor, WysiwygEditor } from "./editor/tiptap-editor"
import type { EditorPostVersion } from "./post-editor-context"
import { usePostEditor } from "./post-editor-context"
import {
  PostEditorFragment,
  PostEditorProvider,
} from "./post-editor-context/provider"
import {
  addImagesButtonClass,
  articleContentRecipe,
  carouselAreaClass,
  carouselDeleteClass,
  dotSeparatorClass,
  editorContainerClass,
  editorWrapperClass,
  headerImagesClass,
  lastSavedClass,
  statusClass,
  statusTextClass,
  textFieldClass,
  topToolbarMobileWrapperRecipe,
  versionsLinkClass,
} from "./post-item.css"

interface PostData {
  title: string
  description: string | null
  content: string
  imageHeaderUrl: string | null
}

type PostHeaderProps = {
  postId: string
  editor: Accessor<Editor | undefined>
  uploadFolder: Accessor<["post", string]>
  title: Accessor<string>
  description: Accessor<string>
  setTitle: (title: string) => void
  setDescription: (description: string) => void
  imageUrls: Accessor<string[]>
  setImageUrls: (urls: string[]) => void
  editingDisabled: Accessor<boolean>
  isLoading: Accessor<boolean>
}

export function PostItem(props: ParentProps) {
  const params = useParams()
  const { user } = useApplication()
  const getId = createMemo(() => splitUrl(params.postId)!)
  const { LL } = useI18nContext()
  const { paths } = createBlogRouter()
  const getVisibility = usePageVisibility()
  const { isMobile, isMobileKeyboardOpen } = useVirtualKeyboard()

  const PostItemQuery = graphql(
    `query PostItem($id: String!) {
      post(id: $id) {
        id
        ...PostEditorFragment
        ...PostStatusPillFragment
        relatedNotes {
          id
        }
      }
    }`,
    [PostEditorFragment, PostStatusPillFragment],
  )
  const [postQueryResult] = createQuery({
    query: PostItemQuery,
    variables: { id: getId() },
  })

  const pinnedNotes = createMemo(() => {
    return postQueryResult.data?.post?.relatedNotes?.length ?? 0
  })

  const getPost = createFragment(
    PostEditorFragment,
    () => postQueryResult.data?.post,
  )
  const isLoading = createMemo(() => postQueryResult.fetching)

  // This code is here to prevent the editor from being blocked for
  // user input while the queries are refetching via urql's `refetchExchange()`.
  //
  // Not very elegant, but it works.
  const isInitialStale = createMemo(() => postQueryResult.stale)

  const [hasCompletedInitialStale, setHasCompletedInitialStale] =
    createSignal(false)

  createEffect(() => {
    if (!hasCompletedInitialStale() && !isInitialStale()) {
      setHasCompletedInitialStale(true)
    }
  })

  const isStale = createMemo(() => {
    if (hasCompletedInitialStale()) return false
    return isInitialStale()
  })

  const [editor, setEditor] = createSignal<Editor>()
  const [activeLocale, setActiveLocale] = createSignal<Locale | null>(null)
  const [isDirty, setIsDirty] = createSignal(false)
  const [isRegenerating, setIsRegenerating] = createSignal(false)
  const [editorInitialized, setEditorInitialized] = createSignal(false)
  const [focusMode, setFocusMode] = createSignal(false)

  const [title, setTitle] = createSignal("")
  const [description, setDescription] = createSignal("")
  const [imageUrls, setImageUrls] = createSignal<string[]>([])

  const SavePostMutation = graphql(
    `
    mutation SavePost($id: ID!, $locale: Locale, $input: SavePostInput!) {
      savePost(id: $id, locale: $locale, input: $input) {
        ... on MutationSavePostSuccess {
          data {
            ...PostEditorFragment
          }
        }
        ...ErrorFragment
      }
    }
  `,
    [PostEditorFragment, ErrorFragment],
  )
  const [savePostState, savePost] = createMutationWithErrorHandling(
    SavePostMutation,
    "savePost",
  )

  const GenerateTranslationMutation = graphql(
    `
    mutation GenerateTranslation($postId: ID!, $locale: Locale!) {
      generateTranslation(postId: $postId, locale: $locale) {
        ... on MutationGenerateTranslationSuccess {
          data {
            ...PostEditorFragment
          }
        }
        ...ErrorFragment
      }
    }
  `,
    [PostEditorFragment, ErrorFragment],
  )
  const [_generateState, generateTranslation] = createMutationWithErrorHandling(
    GenerateTranslationMutation,
    "generateTranslation",
  )

  const MarkTranslationAsUpdatedMutation = graphql(
    `
    mutation MarkTranslationAsUpdated($postId: ID!, $locale: Locale!) {
      markTranslationAsUpdated(postId: $postId, locale: $locale) {
        ... on MutationMarkTranslationAsUpdatedSuccess {
          data {
            ...PostEditorFragment
          }
        }
        ...ErrorFragment
      }
    }
  `,
    [PostEditorFragment, ErrorFragment],
  )
  const [_markState, markTranslationAsUpdated] =
    createMutationWithErrorHandling(
      MarkTranslationAsUpdatedMutation,
      "markTranslationAsUpdated",
    )

  function updateEditorContent(content: PostData) {
    setTitle(content.title)
    setDescription(content.description ?? "")
    editor()
      ?.chain()
      .setMeta("programmaticUpdate", true)
      .setMeta("addToHistory", false)
      .setContent(parseEditorContent(content.content))
      .run()
    setImageUrls(getImageHeaderUrls(content.imageHeaderUrl))
  }

  createEffect(() => {
    if (editorInitialized()) return

    const ed = editor()
    const postData = getPost()

    if (!ed || ed.isDestroyed || !postData) return
    if (!postQueryResult.stale) setEditorInitialized(true)

    const mainLocale = postData.mainLocale
    const currentActiveLocale = activeLocale()
    const availableLocales = postData.availableLocales || []

    const shouldResetLocale =
      !currentActiveLocale || !availableLocales.includes(currentActiveLocale)
    const targetLocale = shouldResetLocale ? mainLocale : currentActiveLocale

    if (shouldResetLocale) {
      setActiveLocale(targetLocale)
    }

    const versions = postData.currentVersions
    const version = findVersionForLocale(versions, targetLocale)
    const newContent = version?.content ?? defaultJsonContent
    const currentContent = JSON.stringify(ed.getJSON())

    setImageUrls(getImageHeaderUrls(version?.imageHeaderUrl))

    if (newContent !== currentContent) {
      updateEditorContent({
        title: version?.title ?? "",
        description: version?.description ?? null,
        content: newContent,
        imageHeaderUrl: version?.imageHeaderUrl ?? null,
      })
    }
  })

  let editorListenerSetup = false

  createEffect(() => {
    const ed = editor()
    if (!ed || ed.isDestroyed || editorListenerSetup) return

    editorListenerSetup = true

    const transactionHandler = ({
      transaction,
    }: {
      transaction: Transaction
    }) => {
      if (
        transaction.getMeta("programmaticUpdate") ||
        !transaction.docChanged
      ) {
        return
      }
      setIsDirty(true)
    }

    ed.on("transaction", transactionHandler)

    onCleanup(() => {
      ed.off("transaction", transactionHandler)
      editorListenerSetup = false
    })
  })

  createEffect(() => {
    const ed = editor()
    if (!ed || ed.isDestroyed) return
    ed.setEditable(!isStale())
  })

  async function save() {
    const locale = activeLocale()
    const post = getPost()
    if (!locale || !post || !isDirty()) return

    setIsDirty(false)

    const data: PostData = {
      title: title(),
      description: description(),
      content: JSON.stringify(editor()?.getJSON() ?? {}),
      imageHeaderUrl: imageUrls().join(",") || null,
    }

    await savePost({
      id: post.id,
      locale,
      input: {
        title: data.title,
        description: data.description ?? "",
        content: data.content,
        imageHeaderUrl: data.imageHeaderUrl ?? "",
      },
    })
  }

  const scheduledSave = createScheduledAsync(save, {
    wait: 2_000,
    maxWait: 30_000,
  })

  createEffect(
    on(
      isDirty,
      dirty => {
        if (!dirty) return
        scheduledSave.schedule()
      },
      { defer: true },
    ),
  )

  async function switchToLocale(targetLocale: Locale) {
    const post = getPost()
    if (!post) return
    await scheduledSave.flush()

    const versions = post.currentVersions
    const version = findVersionForLocale(versions, targetLocale)
    if (version) {
      updateEditorContent(createPostDataFromVersion(version))
      setActiveLocale(targetLocale)
    }
  }

  async function regenerateActiveTranslation() {
    const post = getPost()
    const locale = activeLocale()
    if (!post || !locale) return

    await scheduledSave.flush()
    setIsRegenerating(true)

    const result = await generateTranslation({
      postId: post.id,
      locale,
    })

    if (result.success) {
      toast.success(LL().post.translationRegenerated())

      const updatedPost = getPost()
      if (!updatedPost) return

      const versions = updatedPost.currentVersions
      const version = findVersionForLocale(versions, locale)
      if (version) {
        updateEditorContent(createPostDataFromVersion(version))
      }
    }
    setIsRegenerating(false)
  }

  async function markActiveTranslationAsUpdated() {
    const post = getPost()
    const locale = activeLocale()
    if (!post || !locale) return

    await markTranslationAsUpdated({ postId: post.id, locale })
  }

  const isActiveLocaleOutdated = createMemo(() => {
    const post = getPost()
    const locale = activeLocale()
    if (!post || !locale) return false

    const mainLocale = post.mainLocale
    if (locale === mainLocale) return false

    const versions = post.currentVersions
    const mainVersion = findVersionForLocale(versions, mainLocale)
    const currentVersion = findVersionForLocale(versions, locale)

    if (!mainVersion || !currentVersion) return false

    return isVersionOutdated({ version: currentVersion, mainVersion })
  })

  const isSaving = createMemo(() => savePostState.fetching)
  const isAutoSaving = createMemo(() => isDirty() || isSaving())
  const editingDisabled = createMemo(() => isStale())

  const { editor: editorInstance, wysiwygProps } = createEditor({
    userName: user.publication?.name ?? "",
    uploadFolder: () => ["post", getId()],
    onUpdate: () => {},
    blogLocale: user.publication?.locale,
    autoFocusOnCreate: false,
  })

  const editorIsEmpty = useEditorIsEmpty(() => editorInstance())

  createEffect(() => {
    const ed = editorInstance()
    if (ed) setEditor(ed)
  })

  const previewUrl = createMemo(() => {
    const post = getPost()
    if (!post) return null
    const url = post.previewUrl
    return url ?? null
  })

  const availableLocales = createMemo(() => getPost()?.availableLocales ?? [])

  const drawer = createMemo<DrawerItem[]>(() => {
    const translationsCount = availableLocales().length - 1
    const post = getPost()

    return [
      {
        iconName: "glasses",
        label: LL().post.proofread(),
        description: LL().post.proofreadDescription(),
        href: paths().post().proofread().url,
      },
      {
        iconName: "languages",
        label: LL().post.translations(),
        description: LL().post.translationsDescription(),
        href: paths().post().translations(),
        ...(translationsCount > 0 && { count: translationsCount }),
      },
      {
        iconName: "notebook-text",
        label: LL().post.notes(),
        description: LL().post.notesDescription(),
        href: paths().post().notes().url,
        count: pinnedNotes(),
      },
      {
        iconName: "user",
        label: LL().postSettings.audienceTitle(),
        description: LL().postSettings.audienceDescription(),
        href: paths().post().audience(),
      },
      {
        iconName: "share",
        label: LL().post.share(),
        description: LL().post.shareDescription(),
        href: paths().post().share(),
        disabled: !post?.publishedAt,
      },
    ] satisfies DrawerItem[]
  })

  async function onLeave() {
    await scheduledSave.flush()
  }

  useBeforeLeave(onLeave)

  createEffect(function createVersionOnChangeTabs(prevVisible: boolean) {
    const visible = getVisibility()
    if (prevVisible === !visible) {
      onLeave()
    }
    return visible
  }, getVisibility())

  createEventListener(window, "beforeunload", (event: BeforeUnloadEvent) => {
    if (!isDirty()) return
    event.preventDefault()
  })

  const wordCount = createEditorTransaction(editorInstance, editor => {
    if (!editor) return 0
    return editor.storage.characterCount.words()
  })

  const languageOptions = createMemo(() => {
    const post = getPost()
    if (!post) return []

    const mainLocale = post.mainLocale
    const versions = post.currentVersions
    const mainVersion = findVersionForLocale(versions, mainLocale)

    return availableLocales().map(locale => {
      const version = findVersionForLocale(versions, locale)
      const isMain = locale === mainLocale
      const isActive = locale === activeLocale()

      const isOutdated = isMain
        ? false
        : version && mainVersion
          ? isVersionOutdated({ version, mainVersion })
          : false

      return {
        locale,
        isMain,
        isActive,
        isOutdated,
      }
    })
  })

  const showLanguageBanner = createMemo(() => availableLocales().length > 1)
  const defaultSubscribeFormId = createMemo(() => {
    const forms = getPost()?.publication.leadMagnetForms ?? []
    return forms[0]?.id ?? null
  })
  const subscribeForms = createMemo(() => {
    const forms = getPost()?.publication.leadMagnetForms ?? []
    return forms.map(form => ({
      id: form.id,
      name: form.name,
      content: form.content,
    }))
  })

  return (
    <PostEditorProvider
      value={{
        post: () => postQueryResult.data?.post ?? undefined,
        editor,
        activeLocale,
        isLoading,
        isStale,
        editingDisabled,
        isDirty,
        isSaving,
        isAutoSaving,
        switchToLocale,
      }}
    >
      <MainLayout
        hue="amber"
        header={
          <Box>
            <Box
              alignItems="center"
              justifyContent="space-between"
              flexDirection="row"
              gap="24"
            >
              <LinkButton hue="amber" kind="ghost" href={paths().url}>
                {LL().common.back()}
              </LinkButton>
              <Box gap="12" flexDirection="row" alignItems="center">
                <Tooltip
                  title={LL().post.settings()}
                  description={LL().post.settingsDescription()}
                >
                  <LinkButton
                    hue="amber"
                    kind="surface"
                    href={paths().post().settings()}
                    iconName="settings"
                  />
                </Tooltip>
                <Tooltip
                  title={LL().post.preview()}
                  description={LL().post.previewDescription()}
                >
                  <LinkButton
                    hue="amber"
                    kind="surface"
                    href={previewUrl() ?? undefined}
                    iconName="eye"
                    target="_blank"
                    disabled={
                      !previewUrl() ||
                      isAutoSaving() ||
                      isLoading() ||
                      isStale()
                    }
                  />
                </Tooltip>
                <MainButton
                  editorIsEmpty={editorIsEmpty() ?? false}
                  save={scheduledSave.flush}
                />
              </Box>
            </Box>
            <Show when={!isMobile() && !focusMode()}>
              <TopToolbar
                editor={editorInstance}
                uploadFolder={() => ["post", getId()]}
                subscribeFormId={defaultSubscribeFormId}
                subscribeForms={subscribeForms}
              />
            </Show>
          </Box>
        }
        rightPanel={children(() => props.children)()}
        drawer={isMobileKeyboardOpen() ? undefined : drawer()}
        footerPadded={false}
        footer={
          <Box flexDirection="column" gap="8">
            <Show when={isMobile()}>
              <div
                class={topToolbarMobileWrapperRecipe({
                  keyboardOpen: isMobileKeyboardOpen(),
                })}
              >
                <TopToolbar
                  editor={editorInstance}
                  uploadFolder={() => ["post", getId()]}
                  subscribeFormId={defaultSubscribeFormId}
                  subscribeForms={subscribeForms}
                />
              </div>
            </Show>
            <Show when={!isMobile()}>
              <Status
                post={postQueryResult.data?.post ?? undefined}
                wordCount={wordCount()}
                isSaving={isAutoSaving()}
              />
            </Show>
          </Box>
        }
        focusMode={focusMode()}
        focusModeButton={
          <div class={focusModeButtonWrapperClass}>
            <Tooltip
              title={LL().post.focusMode()}
              description={LL().post.focusModeDescription()}
              dark
              placement="left"
            >
              <button
                type="button"
                class={itemLinkClass({ mobile: false })}
                onClick={() => setFocusMode(prev => !prev)}
              >
                <Icon color="sand900" name="fullscreen" size="24" />
              </button>
            </Tooltip>
          </div>
        }
      >
        <div class={postContainerClass}>
          <div class={articleContentRecipe({ disabled: editingDisabled() })}>
            <PostHeader
              postId={getId()}
              editor={editorInstance}
              uploadFolder={() => ["post", getId()]}
              title={title}
              description={description}
              imageUrls={imageUrls}
              setTitle={title => setIsDirty(true) && setTitle(title)}
              setDescription={description =>
                setIsDirty(true) && setDescription(description)
              }
              setImageUrls={urls => setIsDirty(true) && setImageUrls(urls)}
              editingDisabled={editingDisabled}
              isLoading={isLoading}
            />
            <Show when={showLanguageBanner()}>
              <LanguageBanner
                languages={languageOptions()}
                onSwitchLocale={switchToLocale}
                isActiveLocaleOutdated={isActiveLocaleOutdated()}
                onRegenerateTranslation={regenerateActiveTranslation}
                onMarkAsUpdated={markActiveTranslationAsUpdated}
                isRegenerating={isRegenerating()}
              />
            </Show>
            <div class={editorContainerClass}>
              <Show
                when={getPost()}
                fallback={
                  <div class={postContentClass}>{LL().common.loading()}</div>
                }
              >
                <div class={editorWrapperClass}>
                  <Box gap="16">
                    <WysiwygEditor {...wysiwygProps} />
                  </Box>
                </div>
              </Show>
            </div>
          </div>
        </div>
      </MainLayout>
    </PostEditorProvider>
  )
}

function MainButton(props: {
  editorIsEmpty?: boolean
  save: () => Promise<void>
}) {
  const { paths } = createBlogRouter()
  const { LL } = useI18nContext()
  const { post, isLoading, isStale, activeLocale } = usePostEditor()

  const PublishPostMutation = graphql(
    `mutation PublishPost($id: ID!, $sendEmail: Boolean!, $sendToAll: Boolean!, $private: Boolean!) {
      publishPost(id: $id, sendEmail: $sendEmail, sendToAll: $sendToAll, private: $private) {
        ... on MutationPublishPostSuccess {
          data {
            ...PostEditorFragment
          }
        }
      }
    }`,
    [PostEditorFragment],
  )
  const [publishPostState, publishPost] = createMutation(PublishPostMutation)
  const ArchivePostMutation = graphql(`
    mutation ArchivePost($id: ID!, $archivedAt: DateTime) {
      archivePost(id: $id, archivedAt: $archivedAt) {
        ... on MutationArchivePostSuccess {
          data {
            id
            archivedAt
          }
        }
      }
    }
  `)
  const [archivePostState, archivePost] = createMutation(ArchivePostMutation)

  const hasPublishedVersion = createMemo(() => {
    const p = post()
    if (!p) return false
    return (p.publishedVersions?.length ?? 0) > 0
  })

  const hasUnpublishedChanges = createMemo(() => {
    const p = post()
    if (!p) return false
    return p.currentVersions.some(v => !v.published)
  })

  const buttonType = createMemo<
    "publish" | "update-inline" | "update-flow" | "unarchive"
  >(() => {
    const p = post()
    if (!p) return "publish"
    if (p.archivedAt) return "unarchive"
    if (hasPublishedVersion()) {
      return p.sentAt ? "update-inline" : "update-flow"
    }
    return "publish"
  })
  const currentVersion = createMemo(() => {
    const locale = activeLocale()
    if (!locale) return null
    const p = post()
    if (!p) return null

    return findVersionForLocale(p.currentVersions, locale)
  })
  const isPublishable = createMemo(() => {
    const loading = isLoading()
    const stale = isStale()

    if (loading || stale) {
      return false
    }

    if (hasPublishedVersion() && !hasUnpublishedChanges()) {
      return false
    }

    const version = currentVersion()
    const hasTitle = (version?.title?.trim().length ?? 0) > 0
    const hasContent = !props.editorIsEmpty

    return hasTitle && hasContent
  })
  const isDisabled = createMemo(() => !isPublishable())
  const publishActive = createMemo(() => publishPostState.fetching)

  const publishHref = createMemo(() => {
    const p = post()
    if (!p) return null

    const postPath = paths().post({
      id: p.id,
      title: currentVersion()?.title ?? "",
    })

    return hasPublishedVersion() ? postPath.update() : postPath.publish()
  })

  async function onUpdate(post: {
    id: string
    private: boolean
    tiers: { id: string }[]
  }) {
    await props.save()
    await publishPost({
      id: post.id,
      sendEmail: false,
      sendToAll: true,
      private: post.private,
    })
    toast.success(LL().post.updated())
  }

  function onUnarchive(post: { id: string }) {
    archivePost({
      id: post.id,
      archivedAt: null,
    })
  }

  return (
    <Show
      when={post()}
      fallback={
        <Button hue="emerald" disabled>
          {LL().post.publish()}
        </Button>
      }
    >
      {post => (
        <Switch>
          <Match when={buttonType() === "unarchive"}>
            <Button
              hue="emerald"
              onClick={() => onUnarchive(post())}
              active={archivePostState.fetching}
            >
              {LL().post.unarchive()}
            </Button>
          </Match>
          <Match when={buttonType() === "update-inline"}>
            <Button
              hue="indigo"
              onClick={() => onUpdate(post())}
              active={publishActive()}
              disabled={isDisabled()}
            >
              {LL().post.update()}
            </Button>
          </Match>
          <Match when={buttonType() === "update-flow"}>
            <LinkButton
              hue="indigo"
              href={isDisabled() ? undefined : (publishHref() ?? undefined)}
              disabled={isDisabled()}
            >
              {LL().post.update()} <Icon name="arrow-right" />
            </LinkButton>
          </Match>
          <Match when={buttonType() === "publish"}>
            <LinkButton
              hue="emerald"
              href={isDisabled() ? undefined : (publishHref() ?? undefined)}
              disabled={isDisabled()}
            >
              {LL().post.publish()} <Icon name="arrow-right" />
            </LinkButton>
          </Match>
        </Switch>
      )}
    </Show>
  )
}

function Status(props: {
  post?: FragmentOf<typeof PostStatusPillFragment>
  wordCount: number
  isSaving: boolean
}) {
  const { LL } = useI18nContext()
  const { paths } = createBlogRouter()

  const post = createMemo(() =>
    props.post ? readFragment(PostStatusPillFragment, props.post) : undefined,
  )

  const status = createMemo(() => getPostStatus(post()))

  const hue = createMemo(() => getStatusHue(status()))

  const text = createMemo(() => {
    switch (status()) {
      case "spam":
        return LL().post.statusSpam()
      case "archived":
        return LL().post.statusArchived()
      case "sent":
        return LL().post.statusSent()
      case "published":
        return LL().post.statusPublished()
      default:
        return LL().post.statusDraft()
    }
  })

  return (
    <div class={statusClass}>
      <Box gap="12" flexDirection="row" alignItems="center">
        <Tooltip description={text()} hue={hue()}>
          <Pills pills={props.post ? [postStatusPill(props.post)] : []} />
        </Tooltip>
        <DotSeparator />
        <div class={statusTextClass}>
          {props.wordCount} {LL().post.words()}
        </div>
        <Show when={post()}>
          {post => (
            <>
              <DotSeparator />
              <div class={lastSavedClass}>
                <Show when={!props.isSaving} fallback={LL().common.saving()}>
                  {LL().post.savedAt({
                    time: format(post().updatedAt, "pp"),
                  })}
                </Show>
              </div>
            </>
          )}
        </Show>
        <DotSeparator />
        <a class={versionsLinkClass} href={paths().post().versions()}>
          <Icon name="history" size="12" />
          {LL().post.versions()}
        </a>
      </Box>
    </div>
  )
}

function DotSeparator() {
  return <span aria-hidden="true" class={dotSeparatorClass} />
}

function PostHeader(props: PostHeaderProps) {
  const { LL } = useI18nContext()
  const [currentIndex, setCurrentIndex] = createSignal(0)

  const { selectFiles } = createFileUploader({
    accept: "image/*",
    multiple: true,
  })

  createEffect(() => {
    const len = props.imageUrls().length
    if (len === 0) setCurrentIndex(0)
    else if (currentIndex() >= len) setCurrentIndex(len - 1)
  })

  async function handleFiles(files: File[]) {
    if (files.length === 0) {
      props.setImageUrls([])
      return
    }

    const dataUrls = await Promise.all(
      files.map(
        file =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result as string)
            reader.onerror = reject
            reader.readAsDataURL(file)
          }),
      ),
    )

    props.setImageUrls([...props.imageUrls(), ...dataUrls])

    await Promise.all(
      files.map(async (file, i) => {
        try {
          const url = await uploadImage(file, ["post", props.postId])
          props.setImageUrls(
            props.imageUrls().map(u => (u === dataUrls[i] ? url : u)),
          )
        } catch {
          props.setImageUrls(props.imageUrls().filter(u => u !== dataUrls[i]))
        }
      }),
    )
  }

  function removeCurrentImage() {
    props.setImageUrls(props.imageUrls().filter((_, i) => i !== currentIndex()))
  }

  function addMoreImages() {
    selectFiles(files => handleFiles(files.map(f => f.file)))
  }

  return (
    <div class={postHeaderClass}>
      <div class={postHeaderContentClass}>
        <TextField
          value={props.title()}
          onChange={title => props.setTitle(title ?? "")}
          class={textFieldClass}
          disabled={props.editingDisabled()}
        >
          <TextField.TextArea
            rows="1"
            placeholder={
              props.isLoading() ? LL().common.loading() : LL().post.title()
            }
            class={postTitleClass}
            autoResize
          />
        </TextField>
        <TextField
          value={props.description()}
          onChange={description => props.setDescription(description ?? "")}
          class={textFieldClass}
          disabled={props.editingDisabled()}
        >
          <TextField.TextArea
            placeholder={LL().post.subtitle()}
            class={postDescriptionClass}
            rows="1"
            autoResize
          />
        </TextField>
      </div>
      <div class={headerImagesClass}>
        <Show
          when={props.imageUrls().length > 0}
          fallback={
            <FileUpload
              onUpload={handleFiles}
              text={LL().post.uploadHeaderImage()}
              hue="amber"
              accept="image/*"
              iconName="image-up"
              multiple
            />
          }
        >
          <div class={carouselAreaClass}>
            <div
              class={carouselTrackClass}
              style={{ transform: `translateX(-${currentIndex() * 100}%)` }}
            >
              <For each={props.imageUrls()}>
                {url => <img src={url} class={carouselSlideClass} alt="" />}
              </For>
            </div>
            <button
              type="button"
              class={carouselDeleteClass}
              onClick={removeCurrentImage}
            >
              <Icon name="trash-2" size="14" />
            </button>
            <Show when={props.imageUrls().length > 1}>
              <button
                type="button"
                class={`${carouselArrowClass} ${carouselArrowLeftClass}`}
                onClick={() =>
                  setCurrentIndex(
                    (currentIndex() - 1 + props.imageUrls().length) %
                      props.imageUrls().length,
                  )
                }
              >
                <Icon name="arrow-left" size="18" />
              </button>
              <button
                type="button"
                class={`${carouselArrowClass} ${carouselArrowRightClass}`}
                onClick={() =>
                  setCurrentIndex(
                    (currentIndex() + 1) % props.imageUrls().length,
                  )
                }
              >
                <Icon name="arrow-right" size="18" />
              </button>
              <div class={carouselDotsClass}>
                <For each={props.imageUrls()}>
                  {(_, index) => (
                    <button
                      type="button"
                      class={carouselDotClass}
                      data-active={index() === currentIndex()}
                      onClick={() => setCurrentIndex(index())}
                    />
                  )}
                </For>
              </div>
            </Show>
          </div>
          <button
            type="button"
            class={addImagesButtonClass}
            onClick={addMoreImages}
          >
            <Icon name="plus" size="14" />
          </button>
        </Show>
      </div>
    </div>
  )
}

function parseEditorContent(content: string) {
  return JSON.parse(content)
}

function findVersionForLocale<T extends { locale: Locale }>(
  versions: readonly T[],
  locale: Locale,
): T | undefined {
  return versions.find(v => v.locale === locale)
}

function isVersionOutdated({
  version,
  mainVersion,
}: {
  version: EditorPostVersion
  mainVersion: EditorPostVersion
}): boolean {
  return !version.translated || version.updatedAt < mainVersion.updatedAt
}

function getStatusHue(status: string) {
  switch (status) {
    case "spam":
      return "rose"
    case "archived":
      return "orange"
    case "sent":
      return "indigo"
    case "published":
      return "emerald"
    default:
      return "sand"
  }
}

function getPostStatus(post?: {
  spamScore?: number | null
  archivedAt?: Date | string | null
  sentAt?: Date | string | null
  publishedAt?: Date | string | null
}) {
  if (!post) return "draft"
  if (typeof post.spamScore === "number" && post.spamScore > 2) return "spam"
  if (post.archivedAt) return "archived"
  if (post.sentAt) return "sent"
  if (post.publishedAt) return "published"
  return "draft"
}

function createPostDataFromVersion(version: EditorPostVersion): PostData {
  return {
    title: version.title,
    description: version.description ?? null,
    content: version.content ?? defaultJsonContent,
    imageHeaderUrl: version.imageHeaderUrl ?? null,
  }
}
